// THE /mcc RESPONSE CACHE'S PERSISTENT HALF -- an IndexedDB store that lets the listing, the solve and the
// per-manufacturing-line best scores SURVIVE A PAGE LOAD.
//
// ManufacturingLineResponseCache keeps those answers in a module-level Map, which dies with the document. So
// every refresh, and every arrival from a link, started from nothing: a section that was fully scored a moment
// ago re-listed, re-solved and re-scored every one of its manufacturing lines before the user could read a
// single star. Each of those best-score requests runs a full solve server-side, and a quote of any size has
// dozens of them -- minutes of "Scoring manufacturing lines…" for answers the browser had already been given.
//
// This module is where those answers are kept instead. It is deliberately NOT the cache's interface: the cache
// stays a synchronous Map (its readers, e.g. the best-score effect's hasCachedResponse check, are synchronous),
// and this store is READ ONCE into that Map before the page mounts and WRITTEN THROUGH in the background after.
//
// STALENESS is the whole reason this is not just "the Map, saved". The Map was safe precisely BECAUSE it died
// on reload: the material, the dieline and the machine prices cannot change without leaving /mcc, and leaving
// dropped the cache. An answer that outlives the document has to carry the state it was computed against, so
// every entry is stamped with the server's fingerprint (Frank::ResponseCacheFingerprint) and is only ever read
// back under an identical one. A repriced machine, an edited dieline or a changed quantity mints a new
// fingerprint, and the page asks the server again rather than quoting yesterday's score.
//
// FAILURE IS NEVER FATAL. Private browsing, a full disk, a blocked upgrade: every path below degrades to
// "no persistence, exactly as before" and reports itself through trackEvent, rather than taking the page down
// with it. A cache is an optimisation; the section still works without one.

const DATABASE_NAME = 'manufacturing_cost_calculator_responses';
const DATABASE_VERSION = 2;
const STORE_NAME = 'responses';
const QUOTE_INDEX = 'by_quote';
const LAST_USED_INDEX = 'by_last_used';
const USER_INDEX = 'by_user';

// How many answers the store may hold across every quote before the least recently used are dropped, and how
// far down the sweep goes when it runs. The bound is on the NUMBER of entries rather than their bytes so the
// sweep can walk the oldest-first cursor and delete as it goes, without ever having to read (and deserialise)
// the entries it is keeping. Most entries are a single best score of a few hundred bytes; a section's listing
// and its solve are the large ones at ~50 KB and ~30 KB. In normal use a quote's own answers are replaced (see
// the fingerprint prune below) rather than accumulated, so this ceiling is only reached by visiting a great
// many different quotes -- at which point the oldest are the ones worth losing.
const MAXIMUM_ENTRIES = 600;
const SWEEP_DOWN_TO_ENTRIES = 400;

// How long an answer may sit unopened before it is thrown away, whatever the count. The ceiling above bounds the
// SPACE this takes; this bounds the TIME a quote's machines, sheets and prices can sit in a browser profile
// nobody is using for it any more. A quote reopened inside a month keeps its scoring; one abandoned for longer
// is a re-score the user will not notice, against data we would rather not still be holding.
const MAXIMUM_AGE_MILLISECONDS = 30 * 24 * 60 * 60 * 1000;

// Report a storage failure without taking the page with it. Every caller below carries on with no persistence,
// so this is the only trace such a failure leaves -- do not remove it to quieten a noisy browser.
const reportFailure = (context, error) => {
  if (typeof trackEvent !== 'function') { return; }
  trackEvent('js_error_happened', {
    exception: `[ManufacturingLineResponseStore#${context}] ${error && error.message ? error.message : error}`,
    user_quote_id: window.user_quote_id
  });
};

const promiseFor = (request) => new Promise((resolve, reject) => {
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

// The open database, or null when this browser will not give us one. Memoised: the page opens it once, and a
// browser that refused once will refuse again -- retrying per write would turn every response into a failed
// open. The promise itself is cached (never rejected), so callers can always await it.
let databasePromise = null;

const openDatabase = () => {
  if (databasePromise) { return databasePromise; }

  databasePromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined' || indexedDB === null) { resolve(null); return; }

    let request;
    try {
      request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    } catch (error) {
      // Safari in private browsing throws from open() itself rather than firing onerror.
      reportFailure('openDatabase', error);
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const database = request.result;
      // REBUILT, not migrated. This is a cache, and the entries an older version left behind carry no user --
      // there is nobody to attribute them to, and leaving one person's quote data in a store that now promises
      // to be per-user would be the version bump defeating itself. The cost is one slow page load.
      if (database.objectStoreNames.contains(STORE_NAME)) { database.deleteObjectStore(STORE_NAME); }

      // Keyed by the user, the fingerprint, the viewing and the request URL together: the same question asked
      // by another person, under a newer fingerprint, or in another language is a DIFFERENT answer, and they
      // may all legitimately sit here until the ones that are no longer anybody's are dropped.
      const store = database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      store.createIndex(QUOTE_INDEX, 'quoteId', { unique: false });
      store.createIndex(LAST_USED_INDEX, 'lastUsedAt', { unique: false });
      store.createIndex(USER_INDEX, 'userId', { unique: false });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => { reportFailure('openDatabase', request.error); resolve(null); };
    // Another tab holding an older version open blocks the upgrade. Do not wait on it -- carry on unpersisted
    // rather than leaving the page's mount gated on a tab the user may never touch again.
    request.onblocked = () => { reportFailure('openDatabase', new Error('upgrade blocked by another tab')); resolve(null); };
  });

  return databasePromise;
};

const transactionFor = (database, mode) => {
  const transaction = database.transaction(STORE_NAME, mode);
  return { transaction, store: transaction.objectStore(STORE_NAME) };
};

// An entry is addressed by all four: WHOSE answers these are, WHICH of them are still valid, WHICH viewing of
// them this is, and the request itself. The user comes first because it is the one part that is never shared --
// two people on one browser can hold answers to the same question and neither may read the other's.
const entryKey = ({ userId, fingerprint, variant }, url) => `${userId}|${fingerprint}|${variant}|${url}`;

// A TRANSACTION NEVER SPANS A `then`. An IndexedDB transaction commits as soon as it goes idle, and whether it
// is still alive in a promise callback depends on the microtask rules being implemented faithfully -- the
// classic IndexedDB-with-promises trap, and one Safari has been unreliable about. A request issued into a
// committed transaction throws TransactionInactiveError, which here would reject the whole hydration and leave
// that browser silently uncached. So each helper below issues ALL of its requests in one synchronous turn, and
// anything that has to wait for a result first opens a new transaction to do its next piece of work.
const readAllForQuote = (database, quoteId) => {
  const { store } = transactionFor(database, 'readonly');
  return promiseFor(store.index(QUOTE_INDEX).getAll(quoteId));
};

// Touch what this page is about to use and delete what it may not, in ONE transaction whose requests are all
// issued together. Touching is what makes the sweep measure "recently opened" rather than "recently written":
// a quote the user keeps coming back to should outlive one they configured once and left.
const refreshAndPrune = (database, { keep, drop }) => new Promise((resolve) => {
  if (keep.length === 0 && drop.length === 0) { resolve(); return; }

  const { transaction, store } = transactionFor(database, 'readwrite');
  transaction.oncomplete = () => resolve();
  transaction.onabort = () => { reportFailure('refreshAndPrune', transaction.error); resolve(); };
  transaction.onerror = () => { reportFailure('refreshAndPrune', transaction.error); resolve(); };

  const touchedAt = Date.now();
  keep.forEach((entry) => store.put({ ...entry, lastUsedAt: touchedAt }));
  drop.forEach((entry) => store.delete(entry.key));
});

// EVERY ANSWER THIS PAGE MAY REUSE: the entries stored under this fingerprint AND this viewing of it.
//
// What is deleted is narrower than what is unusable. An entry under a different FINGERPRINT is wrong -- the
// code, the quote, its dieline or the machine catalogue has moved on -- so it goes, and so does anything
// belonging to another USER, which is not this browser's to hold. An entry under the same fingerprint but a
// different VARIANT is merely the same person's other viewing of answers that are still perfectly valid: this
// quote read in the other language or the other unit. It is not used here and not deleted either. Deleting it
// is what made two locales take turns paying for the same scoring, each load wiping what the last one stored.
//
// Resolves to [] on any failure, which simply means the page fetches as it does today.
export const readPersistedResponses = ({ userId, fingerprint, variant, quoteId }) => openDatabase().then((database) => {
  if (!database) { return []; }

  return readAllForQuote(database, quoteId).then((entries) => {
    // Somebody else's answers to this quote's questions are not read and not kept: whoever is signed in now is
    // who this browser holds answers for. (The key already makes them unreadable; this makes them GONE.)
    const mine = entries.filter((entry) => entry.userId === userId);
    const valid = mine.filter((entry) => entry.fingerprint === fingerprint);
    const keep = valid.filter((entry) => entry.variant === variant);
    const drop = entries.filter((entry) => entry.userId !== userId || entry.fingerprint !== fingerprint);

    return refreshAndPrune(database, { keep, drop })
      .then(() => keep.map((entry) => ({ url: entry.url, data: entry.data })));
  });
}).catch((error) => {
  reportFailure('readPersistedResponses', error);
  return [];
});

// Keep one answer. Fire-and-forget: the in-memory Map already has it, so nothing on screen is waiting for this
// to land, and a store that refuses the write costs the user nothing but the next reload's fetch.
export const persistResponse = ({ userId, fingerprint, variant, quoteId, url, data }) => openDatabase().then((database) => {
  if (!database) { return undefined; }

  const { transaction, store } = transactionFor(database, 'readwrite');
  store.put({
    key: entryKey({ userId, fingerprint, variant }, url),
    userId,
    fingerprint,
    variant,
    quoteId,
    url,
    data,
    lastUsedAt: Date.now()
  });

  return new Promise((resolve) => {
    transaction.oncomplete = () => resolve();
    // A QuotaExceededError lands here. The sweep on the next page load is what makes room; a single lost write
    // is not worth interrupting the page for.
    transaction.onabort = () => { reportFailure('persistResponse', transaction.error); resolve(); };
    transaction.onerror = () => { reportFailure('persistResponse', transaction.error); resolve(); };
  });
}).catch((error) => {
  reportFailure('persistResponse', error);
});

// WHAT THIS BROWSER HAS NO BUSINESS KEEPING: every entry belonging to somebody other than the person signed in
// now, and every entry nobody has opened for MAXIMUM_AGE_MILLISECONDS. Neither is about space -- the count trim
// below is -- they are about a store that outlives the session it was filled in.
//
// Both walks are KEY cursors, so no entry is ever deserialised: only its key is read, and `store.delete` takes
// the primary key straight from the cursor. They are issued into ONE transaction in one synchronous turn, like
// every other helper here.
const dropWhatIsNotOurs = (database, userId) => new Promise((resolve) => {
  const { transaction, store } = transactionFor(database, 'readwrite');
  transaction.oncomplete = () => resolve();
  transaction.onabort = () => { reportFailure('dropWhatIsNotOurs', transaction.error); resolve(); };
  transaction.onerror = () => { reportFailure('dropWhatIsNotOurs', transaction.error); resolve(); };

  const anotherUser = store.index(USER_INDEX).openKeyCursor();
  anotherUser.onsuccess = () => {
    const cursor = anotherUser.result;
    if (!cursor) { return; }

    // The index key IS the userId, so this compares without reading the entry.
    if (cursor.key !== userId) { store.delete(cursor.primaryKey); }
    cursor.continue();
  };

  const tooOld = store.index(LAST_USED_INDEX)
    .openKeyCursor(IDBKeyRange.upperBound(Date.now() - MAXIMUM_AGE_MILLISECONDS));
  tooOld.onsuccess = () => {
    const cursor = tooOld.result;
    if (!cursor) { return; }

    store.delete(cursor.primaryKey);
    cursor.continue();
  };
});

// Housekeeping, run in the background after hydration and never before it: drop what is not ours to keep, then
// -- if the store is still over MAXIMUM_ENTRIES -- the least recently used down to SWEEP_DOWN_TO_ENTRIES. The
// count trim walks the oldest-first cursor and deletes as it goes, so the entries that are KEPT are never read
// back; it costs the deserialisation of only what it is throwing away.
const trimToCeiling = (database) => {
  // Counted in a transaction of its own, so the deleting one below starts fresh rather than being resumed
  // across a `then` -- see the note above readAllForQuote.
  const { store: countingStore } = transactionFor(database, 'readonly');

  return promiseFor(countingStore.count()).then((total) => {
    if (total <= MAXIMUM_ENTRIES) { return undefined; }

    return new Promise((resolve) => {
      const { transaction, store } = transactionFor(database, 'readwrite');
      // Attached BEFORE the walk starts: a completion handler added after the last delete can miss the commit
      // it is waiting for, and leave this promise pending for good.
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => { reportFailure('sweepPersistedResponses', transaction.error); resolve(); };
      transaction.onerror = () => { reportFailure('sweepPersistedResponses', transaction.error); resolve(); };

      // The cursor walk lives entirely inside its own success events, which is what keeps the transaction
      // alive from one step to the next.
      let remaining = total - SWEEP_DOWN_TO_ENTRIES;
      const cursorRequest = store.index(LAST_USED_INDEX).openCursor();
      cursorRequest.onsuccess = () => {
        const cursor = cursorRequest.result;
        if (!cursor || remaining <= 0) { return; }

        cursor.delete();
        remaining -= 1;
        cursor.continue();
      };
    });
  });
};

export const sweepPersistedResponses = ({ userId }) => openDatabase().then((database) => {
  if (!database) { return undefined; }

  return dropWhatIsNotOurs(database, userId).then(() => trimToCeiling(database));
}).catch((error) => {
  reportFailure('sweepPersistedResponses', error);
});

// Forget THIS QUOTE'S answers -- what clearResponseCache means once the cache outlives the page.
//
// Not the whole store. One browser holds many quotes, and "start this section again" must not throw away the
// scoring of a quote the user has not even opened today. Only this user's rows are touched, for the same reason
// nothing else here reads across users.
export const clearPersistedResponses = ({ userId, quoteId }) => openDatabase().then((database) => {
  if (!database) { return undefined; }

  const { transaction, store } = transactionFor(database, 'readwrite');
  // A value cursor rather than a key one: the entry itself says whose it is, and clearing is a rare, deliberate
  // act -- reading a few entries to be sure of that beats picking the owner back out of the key string.
  const quoteEntries = store.index(QUOTE_INDEX).openCursor(IDBKeyRange.only(quoteId));
  quoteEntries.onsuccess = () => {
    const cursor = quoteEntries.result;
    if (!cursor) { return; }

    if (cursor.value.userId === userId) { cursor.delete(); }
    cursor.continue();
  };

  return new Promise((resolve) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => { reportFailure('clearPersistedResponses', transaction.error); resolve(); };
    transaction.onerror = () => { reportFailure('clearPersistedResponses', transaction.error); resolve(); };
  });
}).catch((error) => {
  reportFailure('clearPersistedResponses', error);
});
